import { useDeleteHotel } from '@/api/hotels';
import type { Hotel } from '@/config/objects/hotels';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  StatusPill,
  toast,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import {
  Building2,
  KeyRound,
  Map,
  MapPin,
  Pencil,
  Phone,
  Sparkles,
  Tag,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Building2 },
  { label: 'Tours', path: 'tours', icon: Map },
  { label: 'Amenities', path: 'amenities', icon: Sparkles },
  { label: 'Offers', path: 'offers', icon: Tag },
  { label: 'Access Codes', path: 'access-codes', icon: KeyRound },
  { label: 'Guests', path: 'guests', icon: Users },
] as const;

export function HotelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isInternal } = useRequireOrganization();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Hotel | null>(null);
  const basePath = `/hotels/${id}`;

  const deleteMutation = useDeleteHotel();

  const handleDelete = () => {
    if (!pendingDelete) return;
    const promise = deleteMutation.mutateAsync(pendingDelete.id).then(() => {
      navigate('/hotels');
    });
    toast.promise(promise, {
      loading: 'Deleting hotel...',
      success: 'Hotel deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete hotel',
    });
  };

  return (
    <>
      <EntityDetailShell<Hotel>
        resource="hotels"
        queryKey="Hotel"
        id={id}
        basePath={basePath}
        entityLabel="Hotel"
        tabs={tabs}
        contextKey="hotel"
        hero={{
          category: { label: 'Hotel', icon: Building2 },
          title: (h) => h?.name || 'Hotel',
          imageUrl: (h) => h?.featuredImage?.url ?? null,
          fallbackIcon: Building2,
          imageAlt: (h) => h?.name || 'Hotel',
          meta: (h) => (
            <>
              {(h.neighborhood || h.greaterArea) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {h.neighborhood?.name || h.greaterArea?.name}
                </span>
              )}
              {h.organization && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {h.organization.name}
                </span>
              )}
              {h.phoneNumber && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {h.phoneNumber}
                </span>
              )}
            </>
          ),
          badges: (h) => (h.status ? <StatusPill status={h.status} /> : null),
        }}
        breadcrumbs={(h) => [
          { label: 'Home', href: '/' },
          { label: 'Hotels', href: '/hotels' },
          { label: h?.name || 'Hotel', current: true },
        ]}
        headerActions={(h) =>
          h ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {isInternal && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    setPendingDelete(h);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Hotel"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
